import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit,
} from '@angular/core';
import {
  ContestRubricSection,
  ContestRubricSubmission,
  ContestRubricSubmissionScore,
  isContestRubricSubmissionScoreArray,
  ProjectViewUser,
} from '@freelancer/datastore/collections';
import { UiModule } from '@freelancer/ui';
import { AvatarSize } from '@freelancer/ui/user-avatar';

@Component({
  standalone: true,
  imports: [CommonModule, UiModule],
  selector: 'app-judge-submission-card',
  template: `
    <div class="JudgeHeader mb-xsmall">
      <div class="JudgeHeader-identity">
        <fl-user-avatar
          class="mr-xxsmall"
          [size]="AvatarSize.SMALL"
          [users]="[judge]"
        ></fl-user-avatar>
        <p class="font-bold text-large">{{ judge.displayName }}</p>
      </div>
      <p
        class="font-bold text-large"
        i18n="Overall score label for judge submission"
      >
        Overall: {{ totalScore }}/{{ rubricTotalScore }}
      </p>
    </div>
    @if (submission.comment) {
      <p class="mb-xsmall text-xsmall">{{ submission.comment }}</p>
    }
    @for (section of rubricSections; track section.id) {
      <section class="CriteriaRow mb-xsmall">
        <h2
          i18n="Title for a rubric section"
          class="font-bold text-mid mb-xxsmall"
        >
          Section {{ $index + 1 }}: {{ section.title }}
        </h2>
        @for (item of section.items; track item.id) {
          @let scoreSubmission = scoreByItemId.get(item.id);
          @let score = scoreSubmission?.score ?? 0;
          <div class="CriteriaRow-item mb-xxsmall">
            <div class="CriteriaRow-item-header mb-xxxsmall">
              <p class="font-bold text-small">{{ item.title }}</p>
              @if (item.scoreType === 'number') {
                <p class="font-bold text-small">
                  {{ score }}/{{ item.maxScore }}
                </p>
              } @else if (score > 0) {
                <p
                  class="font-bold text-small"
                  i18n="Boolean rubric item score yes"
                >
                  YES
                </p>
              } @else {
                <p
                  class="font-bold text-small"
                  i18n="Boolean rubric item score no"
                >
                  NO
                </p>
              }
            </div>
            @if (scoreSubmission?.comment; as comment) {
              <p class="text-xsmall">{{ comment }}</p>
            } @else {
              <p
                class="text-xsmall"
                i18n="No comment for rubric item score"
              >
                No additional comments.
              </p>
            }
          </div>
        }
      </section>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./judge-submission-card.component.scss'],
})
export class JudgeSubmissionCardComponent implements OnInit {
  AvatarSize = AvatarSize;
  totalScore = 0;
  scoreByItemId = new Map<number, ContestRubricSubmissionScore>();

  @Input({ required: true }) judge: ProjectViewUser;
  @Input({ required: true }) submission: ContestRubricSubmission;
  @Input({ required: true }) rubricSections: readonly ContestRubricSection[];
  @Input({ required: true }) rubricTotalScore: number;

  ngOnInit(): void {
    const submissionScores = isContestRubricSubmissionScoreArray(
      this.submission.scores,
    )
      ? this.submission.scores
      : [];
    this.scoreByItemId = new Map(
      submissionScores.map(submission => [
        submission.contestRubricItemId,
        submission,
      ]),
    );
    this.totalScore = submissionScores
      .map(submission => submission.score)
      .reduce((sum, score) => sum + score, 0);
  }
}
